import { useState } from 'react';

import classNames from 'classnames';
import { CardCVCElement, CardExpiryElement, CardNumberElement } from 'react-stripe-elements';

import styles from 'src/app/components/Form/TextInput/styles.module.scss';

import { FormError } from '../FormError';

const stripeFieldStyles = {
  base: {
    fontSize: '16px',
    color: '#0F3C6C', // react-stripe-elements cannot handle hsla colors
    fontFamily: 'Circular-Pro, Helvetica, Arial, sans-serif',
  },
  invalid: {
    color: '#EF4931', // react-stripe-elements cannot handle hsla colors
  },
};

export enum FieldOptions {
  cardNumber = 'cardNumber',
  date = 'date',
  cvc = 'cvc',
}

const fieldOptions = {
  [FieldOptions.cardNumber]: CardNumberElement,
  [FieldOptions.date]: CardExpiryElement,
  [FieldOptions.cvc]: CardCVCElement,
};

export interface StripeInputProps {
  apiError?: string;
  stripeFieldType: FieldOptions;
  label: string;
  helperText?: string;
  dispatchFieldFocused: (elementType: string) => void;
  dataCy?: string;
}

const StripeInput = ({
  apiError,
  stripeFieldType,
  label,
  helperText,
  dispatchFieldFocused,
  dataCy,
}: StripeInputProps) => {
  const Field = fieldOptions[stripeFieldType];

  const [error, setError] = useState('');
  const [hasFocus, setHasFocus] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const cardError = apiError || error;

  const handleChange = ({ empty, error }) => {
    empty ? setHasValue(false) : setHasValue(true); //eslint-disable-line
    error ? setError(error.message) : setError(null); //eslint-disable-line
  };
  const handleFocus = ({ elementType }: { elementType: string }) => {
    dispatchFieldFocused(elementType);
    setHasFocus(true);
  };
  const handleBlur = () => {
    setHasFocus(false);
  };

  const errorTextToShow = cardError || helperText;
  const errorId = `error-${name}`;
  return (
    <div
      data-cy={dataCy}
      className={classNames(
        styles.component,
        { [styles.hasFocus]: hasFocus || hasValue },
        { [styles.error]: cardError }
      )}
    >
      <Field
        className={styles.input}
        style={stripeFieldStyles}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={
          hasFocus
            ? (value => {
                switch (value) {
                  case 'cardNumber':
                    return '1234 1234 1234 1234';
                  case 'date':
                    return 'MM / YY';
                  case 'cvc':
                    return 'CVC';
                  default:
                    return '';
                }
              })(stripeFieldType)
            : ''
        }
        aria-describedby={errorId}
      />
      <div className={classNames(styles.label, styles.stripeLabel)}>{label}</div>
      <FormError errorId={errorId} error={errorTextToShow} className={styles.helperText} />
    </div>
  );
};

export { StripeInput };
